/**
 * Internal Algolia Search Functions
 *
 * INTERNAL: These functions call Algolia directly and should ONLY be used
 * within API routes (/api/products/search). All other code should import
 * from ./bffSearch.ts which calls the BFF endpoint.
 *
 * Features:
 * - Single search call per request (using algoliasearch-helper for disjunctive faceting)
 * - Batch search using multipleQueries for reduced quota usage
 * - ISR-cached facets for ID translation (no extra Algolia call)
 * - getObjects for ID-based lookups (doesn't count against quota)
 */

import algoliasearchHelper from 'algoliasearch-helper';
import { BRAND_TRAITS } from '@/lib/cms/config';
import { devWarn } from '@/lib/utils/devLog';
import type { SearchForHits } from 'algoliasearch';
import { getCachedFacetsDeduped, resolveAlgoliaLocaleCode } from './cachedFacets';
import { getAlgoliaClient, getProductIndexName, type SortReplica } from './client';
import { ALGOLIA_CONFIG } from './config';
import {
  algoliaContextFields,
  logAlgoliaError,
  logAlgoliaRequest,
  logAlgoliaResponse,
  summarizeObjectIds,
} from './debugLog';
import { getEyewearFacets, getStrapFacets, getWatchFacets } from './facets';
import { findFacetValueById } from './facetValues';
import {
  buildBaseFilterString,
  filterReleasedOrDraftProducts,
  hasIdFilters,
  ProductConfigurationFilterType,
} from './filters';
import { getSearchSkuAlgoliaParams } from './searchQuery';
import type { ProductType } from './filters';
import type {
  FacetResults,
  FilterState,
  ProductHit,
  SearchParams,
  SearchResult,
  WatchHit,
} from './types';

/**
 * Filter context for product configurator timestamps.
 */
type FilterContext = 'plp' | 'search' | 'components';

/**
 * Extended search params with productType for internal use.
 */
interface InternalSearchParams extends SearchParams {
  /** Product type to filter by (default: 'Watch') */
  productType?: ProductType;

  /**
   * Pre-fetched facet values for ID translation.
   * If provided, avoids an extra fetch to /api/facets.
   * Used when the caller (e.g., orchestrator) already has cached facets.
   */
  facetLookup?: FacetResults;

  /**
   * Filter context for product configurator timestamps.
   * 'plp' for product listing pages, 'search' for search overlay.
   * Default: 'plp'
   */
  filterContext?: FilterContext;
}

/**
 * INTERNAL: Search for products in Algolia directly.
 *
 * DO NOT USE outside of API routes. Use searchProducts from ./bffSearch.ts instead.
 *
 * Makes a SINGLE API call that includes hits, facets, and metadata.
 * Uses algoliasearch-helper for disjunctive faceting.
 *
 * @internal
 */
/**
 * The honest answer for a brand with no Algolia product index: nothing found.
 *
 * Returned rather than thrown. Every caller already handles an empty result —
 * the listing orchestrator's own catch does exactly this — so throwing only
 * bought a console error and a wasted round trip against an index that does not
 * exist.
 */
function emptyProductSearchResult(hitsPerPage: number): SearchResult<WatchHit> {
  return {
    hits: [],
    totalHits: 0,
    totalPages: 0,
    page: 1,
    hitsPerPage,
    facets: {},
    processingTimeMs: 0,
  };
}

/** Filter keys holding a list of values, so a scalar key cannot be refined by mistake. */
type MultiValueFilterKey = {
  [K in keyof FilterState]-?: NonNullable<FilterState[K]> extends string[] ? K : never;
}[keyof FilterState];

/**
 * One disjunctive facet a filter can refine.
 *
 * `facet` is Algolia's attribute name and also the key `findFacetValueById`
 * matches on. `localised` appends the locale suffix, for attributes indexed per
 * language. `translate` is false where the stored values are already final —
 * sizes and lug widths are numeric strings, not translated labels — so passing
 * them through the lookup would be a no-op at best.
 */
interface FacetRefinement {
  readonly filterKey: MultiValueFilterKey;
  readonly facet: string;
  readonly localised?: boolean;
  readonly translate?: false;
}

/**
 * Every facet refinement the search applies, in the order it applied them.
 *
 * These were fourteen near-identical blocks — check the filter, derive the
 * attribute, translate each id, refine — which is where most of this function's
 * cognitive complexity came from. As a table, adding a facet is one line and
 * cannot drift from the shape the others use.
 */
const FACET_REFINEMENTS: readonly FacetRefinement[] = [
  // Watch
  { filterKey: 'collection', facet: 'collection' },
  { filterKey: 'size', facet: 'size', translate: false },
  { filterKey: 'case', facet: 'case', localised: true },
  { filterKey: 'dialColor', facet: 'dial_color', localised: true },
  { filterKey: 'type', facet: 'type', localised: true },
  // Singular pipe-format (BEC-24331)
  { filterKey: 'braceletMaterial', facet: 'bracelet_category_material', localised: true },
  { filterKey: 'braceletColor', facet: 'bracelet_category_color', localised: true },
  // Strap
  { filterKey: 'material', facet: 'material', localised: true },
  { filterKey: 'color', facet: 'color', localised: true },
  // Lug width is locale-independent — a numeric string like "24"
  { filterKey: 'lug', facet: 'lug_size', translate: false },
  { filterKey: 'lugType', facet: 'lug_type', localised: true },
  { filterKey: 'buckle', facet: 'buckle_type', localised: true },
  // Eyewear
  { filterKey: 'frameMaterial', facet: 'frame_material', localised: true },
  { filterKey: 'lensColor', facet: 'lens_color', localised: true },
];

/**
 * Translate a filter id to the full facet value Algolia indexed.
 *
 * Falls back to the id whenever the lookup cannot answer, so an unavailable
 * facet cache degrades to "filter by the raw id" rather than dropping the
 * refinement entirely.
 */
function translateFacetId(
  facetLookup: FacetResults | undefined,
  id: string,
  facetBaseName: string,
  facetAttr: string
): string {
  const facetValues = facetLookup?.[facetAttr];
  if (!facetValues) return id;

  // `||`, not `??`: the original guarded on truthiness, so an empty match falls
  // back to the id rather than refining on an empty string.
  return findFacetValueById(id, facetValues, facetBaseName) || id;
}

/**
 * Apply every active filter as a disjunctive refinement, per FACET_REFINEMENTS.
 *
 * Lives outside the search so the table and the loop that reads it sit together,
 * and so the search body is not dominated by the nesting this needs.
 */
function applyFacetRefinements(
  helper: ReturnType<typeof algoliasearchHelper>,
  filters: FilterState,
  localeCode: string,
  facetLookup: FacetResults | undefined
): void {
  for (const { filterKey, facet, localised, translate } of FACET_REFINEMENTS) {
    const values = filters[filterKey];
    if (!values?.length) continue;

    const attribute = localised ? `${facet}.${localeCode}` : facet;
    for (const value of values) {
      helper.addDisjunctiveFacetRefinement(
        attribute,
        translate === false ? value : translateFacetId(facetLookup, value, facet, attribute)
      );
    }
  }
}

/**
 * Reduce the helper's disjunctive facets to the attributes that were requested.
 *
 * Attributes Algolia returned nothing for are omitted rather than emitted empty,
 * which is what the callers' `Object.keys(facets).length` counts rely on.
 */
function collectFacetResults(
  disjunctiveFacets: { name: string; data?: unknown }[] | undefined,
  facetAttributes: string[]
): FacetResults {
  const facets: FacetResults = {};

  for (const facetName of facetAttributes) {
    const match = disjunctiveFacets?.find((facet) => facet.name === facetName);
    if (match) {
      facets[facetName] = match.data as { [value: string]: number };
    }
  }

  return facets;
}

/**
 * Shape one Algolia response into our result contract.
 *
 * Every field carries a default because the helper types them all optional; that
 * cluster of fallbacks is noise in the search body but the whole job here.
 */
function toSearchResult(
  results: {
    hits?: unknown[];
    nbHits?: number;
    nbPages?: number;
    page?: number;
    hitsPerPage?: number;
    queryID?: string;
    processingTimeMS?: number;
  },
  facets: FacetResults,
  requestedHitsPerPage: number
): SearchResult<WatchHit> {
  return {
    hits: results.hits as WatchHit[],
    totalHits: results.nbHits ?? 0,
    totalPages: results.nbPages ?? 0,
    // Algolia is 0-indexed; ours is 1-indexed
    page: (results.page ?? 0) + 1,
    hitsPerPage: results.hitsPerPage ?? requestedHitsPerPage,
    facets,
    queryID: results.queryID,
    processingTimeMs: results.processingTimeMS ?? 0,
  };
}

/**
 * The facet lookup used to turn filter ids into indexed values.
 *
 * A caller that already holds facets (the orchestrator does) passes them in; we
 * only reach for the ISR-cached endpoint when an id-based filter is actually
 * present, since that is the only case translation matters for.
 *
 * `productType` must be the caller's: each type carries different facets, and
 * the wrong ones return values that match no filter id — which surfaces as "no
 * results" for a perfectly valid selection.
 */
async function resolveFacetLookup(
  provided: FacetResults | undefined,
  filters: FilterState,
  locale: string,
  productType: ProductType
): Promise<FacetResults | undefined> {
  if (provided) return provided;
  if (!hasIdFilters(filters)) return undefined;

  return (await getCachedFacetsDeduped(locale, productType)) || undefined;
}

/**
 * The query-shaped fields both the request and error logs report.
 *
 * Shared so a debug line and its matching error line cannot describe the same
 * search differently — and so their defaults live in one place rather than being
 * restated at each call.
 */
function describeQuery(
  helperState: { query?: string; filters?: string },
  filters: FilterState,
  combinedFilters: string,
  baseFilterString: string
) {
  return {
    query: helperState.query || '',
    filters: helperState.filters ?? combinedFilters ?? null,
    sortBy: filters.sortBy ?? 'relevance',
    algoliaRuleName: filters.algoliaRuleName ?? null,
    baseFilterString: baseFilterString || null,
    customFilter: filters.customFilter ?? null,
  };
}

/** The configurator's filter-type enum for a caller's context string. */
function toFilterContextEnum(filterContext: FilterContext): ProductConfigurationFilterType {
  if (filterContext === 'search') return ProductConfigurationFilterType.SEARCH;
  if (filterContext === 'components') return ProductConfigurationFilterType.COMPONENTS;

  return ProductConfigurationFilterType.PLP;
}

/** `base AND custom`, tolerating either side being absent. */
function combineFilterStrings(base: string, customFilter: string | undefined): string {
  if (!customFilter) return base;

  return base ? `${base} AND ${customFilter}` : customFilter;
}

interface HelperQueryOptions {
  readonly filters: FilterState;
  /** Base filters already combined with any caller-supplied `customFilter`. */
  readonly combinedFilters: string;
  readonly filterContext: FilterContext;
  readonly searchSkuParams: ReturnType<typeof getSearchSkuAlgoliaParams> | null;
}

/**
 * Apply the query, filter string and per-context query parameters.
 *
 * Grouped because these settings are interdependent — the SKU params decide the
 * typo tolerance and the query, which the filter string then has to agree with —
 * and reading them together is what makes that legible.
 */
function configureHelperQuery(
  helper: ReturnType<typeof algoliasearchHelper>,
  { filters, combinedFilters, filterContext, searchSkuParams }: HelperQueryOptions
): void {
  if (searchSkuParams) {
    helper.setQueryParameter('typoTolerance', searchSkuParams.typoTolerance);
    helper.setQueryParameter('queryType', searchSkuParams.queryType);
  }

  // Enable Algolia merchandising rules on PLP pages (BEC-24553)
  if (filterContext === 'plp') {
    helper.setQueryParameter('ruleContexts', ['plp_enabled']);
  }

  if (combinedFilters) {
    helper.setQueryParameter('filters', combinedFilters);
  }

  // `algoliaRuleName` triggers Algolia query rules and takes precedence; a plain
  // query is used otherwise.
  if (filters.algoliaRuleName) {
    helper.setQuery(filters.algoliaRuleName);
  } else if (searchSkuParams?.query) {
    helper.setQuery(searchSkuParams.query);
  }
}

/**
 * The facet set to request, which differs per product type.
 *
 * An explicit `requestedFacets` wins: the orchestrator already knows which
 * facets the page renders, and asking for the full set would cost counts nobody
 * displays.
 */
function resolveFacetAttributes(
  requestedFacets: string[] | undefined,
  productType: ProductType,
  localeCode: string
): string[] {
  if (requestedFacets) return requestedFacets;
  if (productType === 'Strap') return getStrapFacets(localeCode);
  if (productType === 'Eyewear') return getEyewearFacets(localeCode);

  return getWatchFacets(localeCode);
}

export async function searchProductsInternal(
  params: InternalSearchParams
): Promise<SearchResult<WatchHit>> {
  // UG has no Algolia product index — its catalogue is Saleor-only
  // (`BRAND_TRAITS.usesAlgoliaProductIndex`). The five product orchestrators
  // already consult that trait; these paths did not, so every UG page carrying a
  // CollectionGroup card queried a non-existent index, logged an error, and
  // rendered an empty rail that read as a Contentful authoring fault.
  //
  // Gated here rather than at the four call sites so the policy lives in one
  // place and a fifth caller inherits it — same reasoning as
  // `pr-review-lessons.md` R13: move the policy, don't allowlist the instances.
  if (!BRAND_TRAITS.usesAlgoliaProductIndex) {
    devWarn(
      '[Algolia] Skipped: this brand has no Algolia product index, so there are no results to return. A caller needing products here must use a Saleor-backed path.',
      { productType: params.productType ?? 'Watch', filters: params.filters ?? null }
    );
    return emptyProductSearchResult(params.hitsPerPage ?? ALGOLIA_CONFIG.hitsPerPage);
  }

  const {
    locale,
    filters = {},
    hitsPerPage = ALGOLIA_CONFIG.hitsPerPage,
    facets: requestedFacets,
    draftMode = false,
    productType = 'Watch',
    facetLookup: providedFacetLookup,
    filterContext = 'plp',
  } = params;

  const client = getAlgoliaClient({ ssr: true });
  // Resolve the index-backed locale code so facet attributes, disjunctive
  // refinements and counts all target the variant the index actually uses.
  const localeCode = await resolveAlgoliaLocaleCode(locale, productType);

  // Get the appropriate index (base or sorted replica)
  const indexName = getProductIndexName(
    locale,
    filters.sortBy as SortReplica | 'relevance' | undefined
  );

  const facetAttributes = resolveFacetAttributes(requestedFacets, productType, localeCode);

  const facetLookup = await resolveFacetLookup(providedFacetLookup, filters, locale, productType);

  const filterContextEnum = toFilterContextEnum(filterContext);

  // PLP reference filter uses explicit objectID; free-text search uses Breitling SKU params.
  const sku = filters.sku;
  const searchSkuParams = sku
    ? null
    : getSearchSkuAlgoliaParams(filters.query, productType, filterContext);

  // Build the base filter string (non-facet filters: product_type, timestamps, price)
  const baseFilterString = buildBaseFilterString({
    ...filters,
    sku,
    productType: productType,
    draftMode,
    localeCode,
    filterContext: filterContextEnum,
  });

  // Calculate page (Algolia is 0-indexed, we use 1-indexed)
  const page = Math.max(0, (filters.page || 1) - 1);

  // Use algoliasearch-helper for disjunctive faceting
  // This ensures facet counts are calculated correctly for OR filters
  const helper = algoliasearchHelper(client, indexName, {
    // All user-selectable facets are disjunctive (OR logic with proper counts)
    disjunctiveFacets: facetAttributes,
    hitsPerPage,
    maxValuesPerFacet: ALGOLIA_CONFIG.maxFacetValues,
    clickAnalytics: ALGOLIA_CONFIG.clickAnalytics,
    distinct: searchSkuParams?.distinct ?? false,
    // Compute facet counts after deduplication for accurate filter counts
    facetingAfterDistinct: true,
  });

  // Base filters (product_type, timestamps, price), plus customFilter when the
  // caller supplied one — strap length, material and so on. Held here because
  // the debug logs below report it alongside the helper's own state.
  const combinedFilters = combineFilterStrings(baseFilterString, filters.customFilter);
  configureHelperQuery(helper, { filters, combinedFilters, filterContext, searchSkuParams });

  // Set page
  helper.setPage(page);

  applyFacetRefinements(helper, filters, localeCode, facetLookup);

  // Execute search (single API call with disjunctive faceting)
  const helperState = helper.state;

  logAlgoliaRequest('searchProductsInternal', {
    ...algoliaContextFields(locale, indexName),
    ...describeQuery(helperState, filters, combinedFilters, baseFilterString),
    localeCode,
    productType,
    filterContext,
    draftMode,
    hitsPerPage,
    page: page + 1,
    facetAttributes,
    hasFacetLookup: Boolean(facetLookup),
    filterState: filters,
    searchSkuParams,
  });

  try {
    const response = await helper.searchOnce({});
    const results = response.content;

    const facets = collectFacetResults(results.disjunctiveFacets, facetAttributes);

    const searchResult = toSearchResult(results, facets, hitsPerPage);

    logAlgoliaResponse('searchProductsInternal', {
      ...algoliaContextFields(locale, indexName),
      productType,
      totalHits: searchResult.totalHits,
      totalPages: searchResult.totalPages,
      page: searchResult.page,
      hitsReturned: searchResult.hits.length,
      hitsPerPage: searchResult.hitsPerPage,
      processingTimeMs: searchResult.processingTimeMs,
      queryID: searchResult.queryID ?? null,
      sampleObjectIds: summarizeObjectIds(searchResult.hits),
      facetCount: Object.keys(facets).length,
      facetAttributeKeys: facetAttributes,
      facetsWithValues: Object.fromEntries(
        Object.entries(facets).map(([name, values]) => [name, Object.keys(values).length])
      ),
      searchSkuParams,
    });

    return searchResult;
  } catch (error) {
    logAlgoliaError('searchProductsInternal', {
      ...algoliaContextFields(locale, indexName),
      ...describeQuery(helperState, filters, combinedFilters, baseFilterString),
      productType,
      error: error instanceof Error ? error.message : error,
    });
    throw error;
  }
}

// ============================================
// Batch Search Functions (multipleQueries)
// ============================================

/**
 * Search query definition for batch operations.
 */
export interface BatchSearchQuery {
  /** Product type to filter by */
  productType: ProductType;
  /** Search query text */
  query?: string;
  /** Number of hits to return (use 0 for counts only) */
  hitsPerPage?: number;
  /** Enable draft/preview mode */
  draftMode?: boolean;
}

/**
 * Result from a batch search query.
 */
export interface BatchSearchResult {
  /** Product type this result is for */
  productType: ProductType;
  /** Array of matching products */
  hits: ProductHit[];
  /** Total number of matching products */
  totalHits: number;
  /** Facets for filtering (counts reflect current search query) */
  facets: FacetResults;
  /** Processing time in milliseconds */
  processingTimeMs: number;
}

/**
 * INTERNAL: Batch search across multiple product types in a SINGLE API call.
 *
 * Uses Algolia's multipleQueries (client.search) to combine multiple searches
 * into one network request. This is more efficient for:
 * - Fetching counts for multiple tabs (Watch, Strap, Eyewear)
 * - Search overlay autocomplete across product types
 *
 * Billing impact:
 * - 1 Search Request (single API call)
 * - N Search Operations (one per query in the batch)
 *
 * @example
 * ```ts
 * // Get counts for all product types in ONE API call
 * const results = await batchSearchProducts({
 *   locale: 'us-en',
 *   queries: [
 *     { productType: 'Watch', query: 'navitimer', hitsPerPage: 0 },
 *     { productType: 'Strap', query: 'navitimer', hitsPerPage: 0 },
 *     { productType: 'Eyewear', query: 'navitimer', hitsPerPage: 0 },
 *   ],
 * });
 * // Returns: [{ productType: 'Watch', totalHits: 42 }, ...]
 * ```
 *
 * @internal
 */
export async function batchSearchProducts(params: {
  locale: string;
  queries: BatchSearchQuery[];
}): Promise<BatchSearchResult[]> {
  const { locale, queries } = params;

  if (queries.length === 0) {
    return [];
  }

  const client = getAlgoliaClient({ ssr: true });
  const indexName = ALGOLIA_CONFIG.getProductIndex(locale);
  // English-variant resolution is product-type agnostic; probe with 'Watch'.
  const localeCode = await resolveAlgoliaLocaleCode(locale, 'Watch');
  const now = Math.floor(Date.now() / 1000);

  // Get facet attributes for each product type
  const getFacetsForType = (productType: ProductType): string[] => {
    switch (productType) {
      case 'Strap':
        return getStrapFacets(localeCode);
      case 'Eyewear':
        return getEyewearFacets(localeCode);
      default:
        return getWatchFacets(localeCode);
    }
  };

  // Build all search requests
  const batchRequests = queries.map((q) => {
    const searchSkuParams = getSearchSkuAlgoliaParams(q.query, q.productType, 'search');
    const textQuery = searchSkuParams.query ?? '';

    // Build base filters for this product type
    const filters: string[] = [];

    // Product type filter
    filters.push(`product_type:${q.productType}`);

    // Product configurator timestamp filters (unless draft mode)
    if (!q.draftMode) {
      filters.push(`search_release_ts < ${now}`);
      filters.push(`search_expire_ts > ${now}`);
    }

    const filterString = filters.join(' AND ');

    // Get facets for this product type
    const facetAttributes = getFacetsForType(q.productType);

    return {
      debug: {
        productType: q.productType,
        query: textQuery,
        searchSkuParams,
        filters: filterString,
        hitsPerPage: q.hitsPerPage ?? 0,
        facetCount: facetAttributes.length,
        draftMode: q.draftMode ?? false,
      },
      request: {
        indexName,
        query: textQuery,
        hitsPerPage: q.hitsPerPage ?? 0,
        filters: filterString,
        // Request facets for filtering
        facets: facetAttributes,
        // Disable analytics for count-only queries
        analytics: (q.hitsPerPage ?? 0) > 0,
        distinct: searchSkuParams.distinct,
        typoTolerance: searchSkuParams.typoTolerance,
        queryType: searchSkuParams.queryType,
        facetingAfterDistinct: true,
      } satisfies SearchForHits,
    };
  });

  const requests = batchRequests.map(({ request }) => request);

  logAlgoliaRequest('batchSearchProducts', {
    ...algoliaContextFields(locale, indexName),
    localeCode,
    queryCount: queries.length,
    requests: batchRequests.map(({ debug }) => debug),
  });

  try {
    // Execute batch search (SINGLE API call)
    const response = await client.search({ requests });

    const results = response.results.map((result, index) => {
      // Type guard: SearchResponse has nbHits, SearchForFacetValuesResponse doesn't
      if ('nbHits' in result) {
        // Extract facets from response
        const facets: FacetResults = {};
        if (result.facets) {
          for (const [facetName, facetValues] of Object.entries(result.facets)) {
            facets[facetName] = facetValues as { [value: string]: number };
          }
        }

        return {
          productType: queries[index].productType,
          hits: (result.hits || []) as ProductHit[],
          totalHits: result.nbHits ?? 0,
          facets,
          processingTimeMs: result.processingTimeMS ?? 0,
        };
      }
      // Fallback for unexpected response type
      return {
        productType: queries[index].productType,
        hits: [],
        totalHits: 0,
        facets: {},
        processingTimeMs: 0,
      };
    });

    logAlgoliaResponse('batchSearchProducts', {
      ...algoliaContextFields(locale, indexName),
      results: results.map((result) => ({
        productType: result.productType,
        totalHits: result.totalHits,
        hitsReturned: result.hits.length,
        processingTimeMs: result.processingTimeMs,
        sampleObjectIds: summarizeObjectIds(result.hits),
        facetCount: Object.keys(result.facets).length,
      })),
    });

    return results;
  } catch (error) {
    logAlgoliaError('batchSearchProducts', {
      ...algoliaContextFields(locale, indexName),
      queryCount: queries.length,
      error: error instanceof Error ? error.message : error,
    });
    throw error;
  }
}

/**
 * INTERNAL: Get product counts for multiple product types in a SINGLE API call.
 *
 * Convenience wrapper around batchSearchProducts for count-only queries.
 * Uses hitsPerPage: 0 to avoid fetching actual hits.
 *
 * @example
 * ```ts
 * const counts = await getProductCountsBatch('us-en', 'navitimer', ['Watch', 'Strap', 'Eyewear']);
 * // Returns: { Watch: 42, Strap: 15, Eyewear: 3 }
 * ```
 *
 * @internal
 */
export async function getProductCountsBatch(
  locale: string,
  query: string,
  productTypes: ProductType[],
  draftMode = false
): Promise<Record<ProductType, number>> {
  const results = await batchSearchProducts({
    locale,
    queries: productTypes.map((productType) => ({
      productType,
      query,
      hitsPerPage: 0,
      draftMode,
    })),
  });

  const counts: Partial<Record<ProductType, number>> = {};
  for (const result of results) {
    counts[result.productType] = result.totalHits;
  }

  return counts as Record<ProductType, number>;
}

/**
 * Get products by their object IDs.
 *
 * This uses getObjects which does NOT count against the search quota.
 * Use this for:
 * - PDP pages
 * - Related products
 * - Wishlist items
 * - Cart items
 *
 * @param locale - Locale string
 * @param objectIds - Array of object IDs (SKUs) to fetch
 * @returns Array of products (null for not found)
 */
export async function getProductsByIds<T extends ProductHit = WatchHit>(
  locale: string,
  objectIds: string[]
): Promise<(T | null)[]> {
  if (objectIds.length === 0) {
    return [];
  }

  const client = getAlgoliaClient({ ssr: true });
  const indexName = ALGOLIA_CONFIG.getProductIndex(locale);

  logAlgoliaRequest('getProductsByIds', {
    ...algoliaContextFields(locale, indexName),
    objectIds,
    count: objectIds.length,
  });

  try {
    const response = await client.getObjects<T>({
      requests: objectIds.map((objectID) => ({
        indexName,
        objectID,
      })),
    });

    const results = response.results.map((result) => {
      // getObjects returns the object or null if not found
      if (result && typeof result === 'object' && 'objectID' in result) {
        return result as T;
      }
      return null;
    });

    logAlgoliaResponse('getProductsByIds', {
      ...algoliaContextFields(locale, indexName),
      requested: objectIds.length,
      found: results.filter(Boolean).length,
      missingObjectIds: objectIds.filter((id, index) => results[index] === null),
      sampleObjectIds: summarizeObjectIds(results),
    });

    return results;
  } catch (error) {
    logAlgoliaError('getProductsByIds', {
      ...algoliaContextFields(locale, indexName),
      objectIds,
      error: error instanceof Error ? error.message : error,
    });
    throw error;
  }
}

/**
 * Get a single product by its object ID.
 *
 * Convenience wrapper around getProductsByIds for single product fetch.
 * Does NOT count against search quota.
 *
 * @param locale - Locale string
 * @param objectId - Object ID (SKU) to fetch
 * @returns Product or null if not found
 */
export async function getProductById<T extends ProductHit = WatchHit>(
  locale: string,
  objectId: string
): Promise<T | null> {
  const results = await getProductsByIds<T>(locale, [objectId]);
  return results[0] ?? null;
}

/**
 * Check if a product is active based on product configurator timestamps.
 *
 * @param hit - Product hit to check
 * @param draftMode - Whether to check preview flag
 * @returns Object with activation status for different contexts
 */
/**
 * Fetch products by IDs with release-window filtering baked in.
 *
 * Wraps `getProductsByIds` + `filterReleasedOrDraftProducts` so callers
 * get only currently-visible products without knowing about the configurator.
 */
export async function getReleasedProductsByIds<T extends ProductHit = WatchHit>(
  locale: string,
  objectIds: string[],
  filterType: ProductConfigurationFilterType = ProductConfigurationFilterType.PLP,
  draftMode?: boolean
): Promise<T[]> {
  const hits = await getProductsByIds<T>(locale, objectIds);
  return filterReleasedOrDraftProducts(
    hits.filter((h): h is T => h !== null),
    filterType,
    undefined,
    draftMode
  );
}

/**
 * Branded sentinel for Algolia fetch failures in PDP gates.
 *
 * Distinguishes "product not in index" (null) from "Algolia network/config error"
 * so the PDP gate can fail open on transient errors instead of returning false 404s.
 */
export const ALGOLIA_FETCH_FAILED: unique symbol = Symbol('ALGOLIA_FETCH_FAILED');
export type AlgoliaFetchFailed = typeof ALGOLIA_FETCH_FAILED;

export function checkProductActivation(
  hit: ProductHit | null | undefined,
  draftMode = false
): {
  isPdpActivated: boolean;
  isPlpActivated: boolean;
  isSearchActivated: boolean;
} {
  const useProductConfigurator = process.env.NEXT_PUBLIC_USE_PRODUCT_CONFIGURATOR === 'true';

  if (!hit) {
    return {
      isPdpActivated: false,
      isPlpActivated: false,
      isSearchActivated: false,
    };
  }

  if (!useProductConfigurator) {
    return {
      isPdpActivated: true,
      isPlpActivated: true,
      isSearchActivated: true,
    };
  }

  const now = Math.floor(Date.now() / 1000);

  const checkTimestamps = (releaseTs: number | null, expireTs: number | null): boolean => {
    // Use loose equality (== null) to catch both null AND undefined.
    // Algolia returns undefined for fields that don't exist on a record,
    // even though the TypeScript type declares them as `number | null`.
    if (releaseTs == null || expireTs == null) return true;
    return releaseTs < now && expireTs > now;
  };

  // Algolia is the PDP visibility source of truth. `pdp_enabled: false` 404s
  // unless CMS draft preview overrides (`draftMode && pdp_preview`). Absent/
  // undefined keeps existing records visible. Saleor channel only scopes price/CTA.
  const isPdpPreviewOverride = Boolean(draftMode && hit.pdp_preview);

  return {
    isPdpActivated:
      (hit.pdp_enabled !== false && checkTimestamps(hit.pdp_release_ts, hit.pdp_expire_ts)) ||
      isPdpPreviewOverride,
    isPlpActivated:
      checkTimestamps(hit.plp_release_ts, hit.plp_expire_ts) || (draftMode && hit.plp_preview),
    isSearchActivated:
      checkTimestamps(hit.search_release_ts, hit.search_expire_ts) ||
      (draftMode && hit.search_preview),
  };
}

/**
 * Filter bundle variation SKUs to only include enabled/activated products.
 *
 * Used for the "Change Strap" modal to filter out products that are not
 * currently available (e.g., expired, not yet released, or draft-only).
 *
 * @param locale - Locale string for the index
 * @param skus - Array of bundle variation SKUs to check
 * @param draftMode - Whether to include draft/preview products
 * @returns Array of enabled SKUs (filtered to only active products)
 */
export async function filterEnabledBundleVariations(
  locale: string,
  skus: string[],
  draftMode = false
): Promise<string[]> {
  if (skus.length === 0) {
    return [];
  }

  // Fetch all products by ID (doesn't count against search quota)
  const hits = await getProductsByIds<WatchHit>(locale, skus);

  // Filter to only include products that are activated for PDP
  return hits
    .filter((hit): hit is WatchHit => {
      if (!hit) return false;
      const activation = checkProductActivation(hit, draftMode);
      return activation.isPdpActivated;
    })
    .map((hit) => hit.objectID as string);
}
